/* eslint-disable import/prefer-default-export */

import isFunction from 'lodash/isFunction';

import React, {
  useEffect,
  useState,
  useRef,
} from 'react';
import styled, { css } from 'styled-components';

// import AntdIcon from 'antd/lib/icon';
import {
  UploadOutlined,
} from '@ant-design/icons';

import { Redirect } from 'react-router-dom';

import Admin from './Admin';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  ${({ $disabled }) => $disabled && css`
    cursor: not-allowed;
    filter: grayscale(1);
    opacity: 0.5;
    * {
      pointer-events: none !important;
    }
  `}
`;

const getDashedBackgroundSrc = ({
  color = 'black',
  radius = 0,
  width = 2,
  dashArray1 = 6,
  dashArray2,
} = {}) => {
  radius = parseInt(radius, 10);
  width = parseInt(width, 10);
  dashArray1 = parseInt(dashArray1, 10);
  dashArray2 = (
    typeof dashArray2 === 'undefined'
    ? Math.round(dashArray1 * 1.3)
    : parseInt(dashArray2, 10)
  );
  // eslint-disable-next-line max-len
  return `data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='${
    radius
  }' ry='${
    radius
  }' stroke='${
    encodeURIComponent(color)
  }' stroke-width='${
    width
  }' stroke-dasharray='${
    dashArray1
  }%2c ${
    dashArray2
  }' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e`;
};

// eslint-disable-next-line max-len
const UploadArea = styled.label`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100px;
  border-radius: ${({ theme }) => theme.less.borderRadius};
  /* border: 1px dashed ${({ theme }) => theme.less.borderColor}; */
  background-image: ${({ theme }) => `url("${getDashedBackgroundSrc({
    color: theme.less.borderColor,
    radius: theme.less.borderRadius,
  })}")`};
  cursor: pointer;
  ${({ $disabled }) => (
    $disabled
    ? css`
        pointer-events: none !important;
        * {
          pointer-events: none !important;
        }
        > * {
          opacity: 0.5;
        }
      `
    : css`
        color: ${({ theme }) => theme.less.primaryColor};
      `
  )}
`;

const UploadAreaIconWrapper = styled.div`
  svg {
    opacity: 0.7;
    font-size: 40px;
  }
`;

const UploadAreaText = styled.p`
  margin: 0px;
  margin-top: 5px;
`;

const UploadInput = styled.input`
  opacity: 0;
  position: absolute;
  top: 0px;
  left: 0px;
  bottom: 0px;
  right: 0px;
  cursor: pointer;
`;

function Upload(props) {
  const {
    client,
    ready,
    storeId,
    redirectOnCreate = true,
    onCreate,
  } = props;
  const propsRef = useRef();
  propsRef.current = props;
  const [file, setFile] = useState(null);
  const [importId, setImportId] = useState(null);
  useEffect(
    () => {
      let cancelled = false;
      if (file && storeId) {
        const form = new FormData();
        form.set('file', file, file.name || 'products.csv');
        client.request({
          url: `/products-imports${
            storeId
            ? `?store=${storeId}`
            : ''
          }`,
          method: 'POST',
          data: form,
        })
        .then(async (response) => {
          if (!cancelled && response?.data?._id) {
            await new Promise(resolve => setTimeout(resolve, 500));
            Admin.showMessage(
              'CSV Uploaded and will be processed shortly',
              'success',
              3,
            );
            setImportId(response.data._id);
            if (onCreate) {
              onCreate(response.data, propsRef.current);
            }
          }
          if (!cancelled) {
            setFile(null);
          }
        })
        .catch(async (error) => {
          if (!cancelled) {
            await new Promise(resolve => setTimeout(resolve, 500));
            error.message = error.response?.data?.message || error?.message;
            Admin.showMessage(
              `CSV Upload Failed: ${error.message}`,
              'error',
              5,
            );
            // setValue('');
            if (!cancelled) {
              setFile(null);
            }
          }
          console.log(error); // eslint-disable-line no-console
        });
      }
      return () => {
        cancelled = true;
      };
    },
    [ready, storeId, file, setFile, client, onCreate],
  );
  return (
    <UploadArea style={props.style}>
      {
        (importId && redirectOnCreate)
        ? <Redirect to={`/products-imports/${importId}`} />
        : null
      }
      <UploadAreaIconWrapper>
        {
          props.getIcon
          ? props.getIcon(props)
          : <UploadOutlined />
        }
      </UploadAreaIconWrapper>
      <UploadAreaText>
        {
            props.getPlaceholder
          ? props.getPlaceholder(props)
          : (props.placeholder || null)
        }
      </UploadAreaText>
      <UploadInput
        type="file"
        accept="text/csv"
        value=""
        onChange={async (ev) => {
          const { target: { files } } = ev;
          if (files && files[0]) {
            setFile(files[0]);
          } else {
            setFile(null);
          }
        }}
      />
    </UploadArea>
  );
}

const FIELD_PRODUCTS_CSV_IMPORT_DEFAULT_CONFIG = {
  getStoreId: props => props.form?.getFieldValue?.('store'),
};

Admin.addToLibrary(
  'FieldProductsImportCsv',
  (config = {}) => Admin.compileFromLibrary(['FieldReact', {
    name: 'upload',
    placeholder: 'Drag and drop your csv file or click to select one',
    label: null,
    ...FIELD_PRODUCTS_CSV_IMPORT_DEFAULT_CONFIG,
    ...config,
    ContentComponent: (props) => {
      const storeId = props.getStoreId(props);
      const disabled = (
          isFunction(props.isDisabled)
        ? props.isDisabled(props)
        : false
      );
      return (
        <Wrapper $disabled={disabled}>
          <Upload
            {...props}
            storeId={storeId}
            // client={props.client}
            // viewer={props.viewer}
            // storeId={props.form.getFieldValue('store')}
            // redirectOnCreate={props.redirectOnCreate}
            // onCreate={props.onCreate}
            // ready={false}
          />
        </Wrapper>
      );
    },
  }]),
);

Admin.addToLibrary(
  'ActionProductsImport',
  ({
    productsImportCsvConfig = {},
    ...config
  }) => (
    Admin.compileFromLibrary(['ActionWithFormModal', {
      name: 'products-import',
      label: null,
      title: 'Upload CSV',
      icon: 'upload',
      type: 'default',
      // style: { marginLeft: '10px' },
      // eslint-disable-next-line arrow-body-style, no-unused-vars
      canToggleModalOpen: (props, action, newVisible) => {
        // return newVisible === true;
        return true;
      },
      modalProps: {
        centered: true,
        closable: true,
        footer: false,
        destroyOnClose: true,
      },
      fields: [
        ['FieldConnectionSelect', {
          name: 'store',
          label: null,
          // prefix: 'Shipping Company:',
          url: '/stores',
          placeholder: 'Choose the Store to upload products for',
          allowClear: true,
          searchPaths: ['name'],
        }],
        ['FieldProductsImportCsv', {
          ...productsImportCsvConfig,
          disabled: [
            [
              'condition.testFieldValue',
              'store',
              (value) => !value,
            ],
          ],
        }],
      ],
      actions: [],
      ...config,
    }])
  ),
);
